/**
 * INSTITUIÇÕES DE ENSINO DA BAHIA — a base oficial e a busca, sem dependências.
 *
 * Funciona igual no navegador, no Node e num servidor: a busca recebe a base já carregada.
 * Quem carrega é o ambiente:
 *   - navegador: carregarBase("/instituicoes-ba.json")  (o arquivo fica em public/ do produto)
 *   - Node:      import { carregarDoDisco } from "@mvpconsultorialtda/instituicoes-ensino/node"
 *
 *   const base = await carregarBase()
 *   base.buscar("uneb")                                   // por nome ou sigla, sem acento
 *   base.buscar("rui barbosa", { nivel: "basica", municipio: "Salvador" })
 *   base.porId("superior:40")                              // UNEB
 *   base.municipios({ nivel: "superior" })
 *   base.resumo()                                          // quantas por nível e por rede
 *
 * Cada instituição: { id, nivel, codigo, nome, sigla, municipio, rede, tipo }
 *   id      "basica:<código INEP>" ou "superior:<código IES>" — único entre os dois níveis
 *   nivel   "basica" | "superior"
 *   rede    "federal" | "estadual" | "municipal" | "privada"
 *   tipo    "escola" (básica) | "universidade" | "centro universitário" | "faculdade" | "instituto federal"
 */

/** Tira acento e caixa, para "São Félix" casar com "sao felix". */
export function normalizar(s) {
  return String(s ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim()
}

/** Monta a base a partir do documento instituicoes-ba.json já lido. Não faz IO. */
export function criarBase(doc) {
  const campos = doc.campos
  const todas = doc.instituicoes.map((linha) => {
    const o = Object.fromEntries(campos.map((c, i) => [c, linha[i]]))
    o._chave = normalizar(`${o.nome} ${o.sigla ?? ""} ${o.municipio}`)
    return o
  })
  const porIdMapa = new Map(todas.map((o) => [o.id, o]))
  const publico = ({ _chave, ...resto }) => resto

  return {
    fonte: doc.fonte,
    geradoEm: doc.geradoEm,

    /**
     * Busca: todas as palavras precisam aparecer no nome, na sigla ou no município.
     * Filtros opcionais: nivel, rede, municipio, tipo. Texto vazio lista com os filtros.
     */
    buscar(texto = "", { nivel, rede, municipio, tipo, limite = 20 } = {}) {
      const palavras = normalizar(texto).split(/\s+/).filter(Boolean)
      const mun = municipio ? normalizar(municipio) : null
      // T26.R11 · quem digita a CIDADE quer as escolas da cidade. O relatório de 09/10: «buscar
      // "Salvador" devolve escolas com "Salvador" no nome em outras cidades». Primeiro as da cidade
      // que o texto nomeia por inteiro; depois as outras que casam — na ordem do arquivo, como antes.
      const texto_ = palavras.join(" ")
      const daCidade = []
      const outras = []
      for (const o of todas) {
        if (nivel && o.nivel !== nivel) continue
        if (rede && o.rede !== rede) continue
        if (tipo && o.tipo !== tipo) continue
        if (mun && normalizar(o.municipio) !== mun) continue
        if (!palavras.every((p) => o._chave.includes(p))) continue
        const cidade = normalizar(o.municipio)
        if (texto_ && (texto_ === cidade || texto_.startsWith(cidade + " ") || texto_.endsWith(" " + cidade))) daCidade.push(o)
        else if (outras.length < limite) outras.push(o)
      }
      return [...daCidade, ...outras].slice(0, limite).map(publico)
    },

    /** A instituição pelo id ("basica:29111111" / "superior:40"), ou null. */
    porId(id) {
      const o = porIdMapa.get(String(id))
      return o ? publico(o) : null
    },

    /** Municípios em ordem, opcionalmente só de um nível. */
    municipios({ nivel } = {}) {
      const s = new Set()
      for (const o of todas) if (!nivel || o.nivel === nivel) s.add(o.municipio)
      return [...s].sort((a, b) => a.localeCompare(b, "pt-BR"))
    },

    /** Contagem por nível e por rede — para a tela mostrar o tamanho da base. */
    resumo() {
      const r = { total: todas.length, porNivel: {}, porRede: {} }
      for (const o of todas) {
        r.porNivel[o.nivel] = (r.porNivel[o.nivel] || 0) + 1
        r.porRede[o.rede] = (r.porRede[o.rede] || 0) + 1
      }
      return r
    },
  }
}

let cargaNavegador = null
/** Navegador: baixa o JSON uma vez (só quando a tela pede) e devolve a base. */
export function carregarBase(url = "/instituicoes-ba.json") {
  if (!cargaNavegador) {
    cargaNavegador = fetch(url)
      .then((r) => { if (!r.ok) throw new Error(`a base de instituições não carregou (${r.status})`); return r.json() })
      .then(criarBase)
      .catch((e) => { cargaNavegador = null; throw e })
  }
  return cargaNavegador
}
